import {
	fetchApiExportAiChatCsv,
	type Filter as CatalogFilter,
} from '@repo/api-codegen';
import { FilterOptionType } from '@repo/common/components/Filter';
import { UserRole } from '@repo/common/enums/UserRole';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router';
import { processSentimentFilter } from '../../api/hooks/aiChats/chatUtils';

import { useSuspenseFeatureAccess } from '../../api';
import type { FetchModelInfiniteListHook } from '../../api/factories/types';
import { useAiChatInfiniteList, type IAiChat } from '../../api/hooks/aiChats';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useColumns } from './AiChatsPage.hooks';
import { AiChatDrawer } from './components/AiChatDrawer';
import { AiChatsPageNavBar } from './components/AiChatsPageNavBar';
import { getAiChatsFilterOptions } from './filters';

function AiChatsPage() {
	const { aiImpactDashboard } = useFeatureFlags();
	const { analyticsAccess } = useSuspenseFeatureAccess();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedChat, setSelectedChat] = useState<IAiChat | null>(null);

	const chatIdFromUrl = searchParams.get('chatId');
	const drawerOpened = !!chatIdFromUrl || !!selectedChat;

	const handleBackButtonClick = () => {
		navigate('/analytics');
	};

	const columns = useColumns();
	const usePaginationList = useAiChatInfiniteList;
	const filterOptions = useMemo(() => getAiChatsFilterOptions(), []);

	const additionalFilters = [
		FilterOptionType.FREQUENT_USERS,
		FilterOptionType.CREATED_TIME,
		FilterOptionType.AI_PERSONA,
		FilterOptionType.TEAMS,
	];

	const handleCellClick = useCallback(
		({
			column,
			record,
		}: {
			column: ExtendedDataTableColumn<IAiChat>;
			record: IAiChat;
		}) => {
			if (column.navigate !== false) {
				setSelectedChat(record);
				setSearchParams({ chatId: record.id });
			}
		},
		[setSearchParams]
	);

	const handleCloseDrawer = useCallback(() => {
		setSelectedChat(null);
		setSearchParams({});
	}, [setSearchParams]);

	const handleServerCsvExport = useCallback(
		async ({
			catalogFilter,
			debouncedSearch,
			sort,
			startJob,
		}: {
			catalogFilter: CatalogFilter | undefined;
			debouncedSearch: string;
			sort: { field: string; order: string } | null;
			startJob: (jobId: string, filename: string) => Promise<void>;
		}) => {
			const { sentimentFilter, filterWithoutSentiment } =
				processSentimentFilter(catalogFilter);

			const serializedFilter =
				filterWithoutSentiment && typeof filterWithoutSentiment === 'object'
					? JSON.stringify(filterWithoutSentiment)
					: undefined;

			let sortOrder: 'oldest' | 'newest' = 'newest';
			if (sort) {
				sortOrder = sort.field === 'created_at' ? 'oldest' : 'newest';
			}

			const response = await fetchApiExportAiChatCsv({
				queryParams: {
					sort_order: sortOrder,
					time_range: '365d',
					...(serializedFilter && { filter: serializedFilter }),
					...(debouncedSearch && { search_term: debouncedSearch }),
					...(sentimentFilter && { sentiment_filter: sentimentFilter }),
				},
			});

			if (response.id) {
				await startJob(response.id, 'ai_chats.csv');
			}
		},
		[]
	);

	if (!aiImpactDashboard) {
		return (
			<EmptyState
				title="AI Chats analytics is not yet enabled"
				description="Please contact your administrator to enable AI Chats analytics"
				illustrationName="ai"
				includeGoBack={true}
				size="lg"
				buttons={[
					{
						name: 'Go back',
						action: () => {
							handleBackButtonClick();
						},
						size: 'md',
						isPrimary: false,
					},
				]}
			/>
		);
	}

	return (
		<FeatureGuard
			featureName="Analytics"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Analytics.Read"
			isFeatureIncludedInPlan={analyticsAccess ?? false}
			isAlwaysIncludedFeature={false}
			description="Understand AI chats and their impact on your workspace."
		>
			<PageLayoutOuterWrapper>
				<Helmet>
					<title>AI Messages</title>
				</Helmet>
				<PageLayoutWrapper name="ai-chats">
					<PageLayoutContentWrapper name="ai-chats">
						<AiChatsPageNavBar />
						<PageLayoutContent>
							<TableV2<IAiChat>
								pluralTypeString="AI chats"
								usePaginationList={
									usePaginationList as FetchModelInfiniteListHook<IAiChat>
								}
								withInfiniteScroll
								withSearch
								withInteractiveHeader
								withCsvExport
								withFilterOptions={filterOptions}
								additionalFilters={additionalFilters}
								columns={columns}
								onCellClick={handleCellClick}
								usePaginationListOptions={{ suspense: true }}
								onAiAnalyticsServerCsvExport={handleServerCsvExport}
							/>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
				</PageLayoutWrapper>
				<AiChatDrawer
					opened={drawerOpened}
					onClose={handleCloseDrawer}
					chatId={chatIdFromUrl}
					selectedChat={selectedChat}
				/>
			</PageLayoutOuterWrapper>
		</FeatureGuard>
	);
}

const ObservedAIChatsPage = observer(AiChatsPage);
export default ObservedAIChatsPage;
